import router from "@/router";
import { defineStore, acceptHMRUpdate } from "pinia";
import { updateInArray } from "./helpers/helpers";
import { ref, computed } from "vue";
import { usePlugins } from "./helpers/usePlugins";
import { Entity } from "@/types/api/common";
import {
  DateRange,
  Market,
  TvRating,
  ratingsForCSVResponse,
} from "@/types/api/TvRating";

import client from "@/api/openapi-ts";
import type { paths, components } from "@/api/schema.d.ts";
import { useErrorStore } from "@/stores/ErrorStore";

type schemas = components["schemas"];

type Loading = {
  stations: boolean;
  programs: boolean;
  comparisonPrograms: boolean;
  ratings: boolean;
  avg_hours: boolean;
  markets: boolean;
  index_audiences: boolean;
  dates: boolean;
  csv: boolean;
  projects: boolean;
  comparison: boolean;
};

type TaskIds = {
  dates?: string | undefined;
  programs?: string | undefined;
  ratings?: string | undefined;
  avg_hours?: string | undefined;
  comparison?: string | undefined;
  comparisonPrograms?: string | undefined;
};

type Errors = {
  avgHours?: string | undefined;
  overview?: string | undefined;
  ratings?: string | undefined;
  daypart?: string | undefined;
  comparison?: string | undefined;
};

type Filters = {
  project_id?: number;
  meta_audience_id?: number;
  geography?: Market;
  index_meta_audience_id?: number;
  date_ranges?: string[];
  tv_categories?: string[];
  genres?: string[];
  day_ranges?: string[];
  dayparts?: string[];
  stations?: any;
  programs?: string[];
  networks?: string[];
  avg_audience_rating?: number[];
  avg_audience_index?: number[];
  audience_impressions?: number[];
  use_sample?: boolean;
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_desc?: boolean;
  languages?: string[];
  excluded_genres?: string[];
  favoriteName?: string;
  includeAdultRating?: boolean;
  comparisonPrograms?: string[];
};

type ChartFilter = {
  day_ranges: string[];
  networks: string[];
  dayparts: string[];
};

type ComparisonFilters = {
  compare: string;
  meta_audience_ids: number[];
  geography?: Market;
  stations: string[][];
  programs?: string[];
};

type Title = {
  audienceName?: string;
  indexAudience?: string;
  market?: string;
  dateSelection?: string;
};
type GetProgramParams = { search?: string };

type TvRatingsData = {
  audience_impressions?: number[];
  avg_audience_index?: number[];
  avg_audience_rating?: number[];
  day_ranges?: { "M-F": number; "Sa-Su": number };
  daypart_networks?: {
    dayparts: { name: string; data: number[] }[];
    networks: string[];
  };
  dayparts?: { daypart_name: string; reach: number }[];
  networks?: { network_name: string; reach: number }[];
  pagination?: { total_items: number; page: number; page_items: number };
  ratings?: TvRating[];
};

type ComparisonData = {
  audience_details: { audience_id: number; name: string }[];
  stations: string[][];
  compare: string;
  categories: string[];
  dayparts: string[];
  data: any;
};

type HrsAndMinutes = { hours: number; minutes: number };

type AvgHours = {
  avg_broadcast_hours?: number;
  avg_cable_hours?: number;
  avg_broadcast_per_week?: HrsAndMinutes;
  avg_cable_per_week?: HrsAndMinutes;
};

type CacheRun = {
  id?: number;
  user_id?: number;
  task_id?: number;
  audience_id?: number;
  dma_id?: number;
  state_id?: number;
  congressional_district_nextelection_id?: number;
  start_date?: string;
  end_date?: string;
  sample_ratio?: number;
  status?: string;
  started_at?: string;
  finished_at?: string;
  created_at?: string;
  updated_at?: string;
};

type Project = {
  id?: number;
  name: string;
  description?: string;
  type?: string;
  cache_runs?: Filters[] | CacheRun[];
  meta_data?: Filters[];
};

const defaultFilterState = {
  meta_audience_id: undefined,
  geography: undefined,
  index_meta_audience_id: undefined,
  date_ranges: [],
  tv_categories: [],
  genres: ["All Programming"],
  day_ranges: [],
  dayparts: [],
  stations: [],
  networks: [],
  avg_audience_rating: [],
  avg_audience_index: [],
  audience_impressions: [],
  use_sample: true,
  page: 1,
  per_page: 25,
  sort_by: "rating",
  sort_desc: true,
};

const defaultComparisonFilterState = {
  compare: "stations",
  meta_audience_ids: [],
  geography: undefined,
  stations: [],
  programs: [],
};

export const useTvRatingStore = defineStore("tvRating", () => {
  const errorStore = useErrorStore();

  const loading = ref<Loading>({
    stations: false,
    programs: false,
    comparisonPrograms: false,
    ratings: false,
    avg_hours: false,
    markets: false,
    index_audiences: false,
    dates: false,
    csv: false,
    projects: false,
    comparison: false,
  });
  const taskIds = ref<TaskIds>({
    dates: undefined,
    programs: undefined,
    ratings: undefined,
    avg_hours: undefined,
    comparison: undefined,
    comparisonPrograms: undefined,
  });

  const errors = ref<Errors>({
    avgHours: undefined,
    overview: undefined,
    ratings: undefined,
    daypart: undefined,
    comparison: undefined,
  });

  const savingProject = ref(false);
  const filters = ref<Filters>(defaultFilterState);
  const comparisonFilters = ref<ComparisonFilters>({
    compare: "stations",
    meta_audience_ids: [],
    geography: undefined,
    stations: [],
    programs: [],
  });
  const title = ref<Title>({});
  const indexAudiences = ref<Entity[]>([]);
  const dates = ref<DateRange | undefined>(undefined);
  const stations = ref<string[][]>([]);
  const programs = ref<string[]>([]);
  const comparisonPrograms = ref<string[]>([]);
  const comparisonStations = ref<string[][]>([]);
  const markets = ref<Market[]>([]);
  const current = ref<Filters>({});
  const previous = ref<Filters>({});
  const ratingsType = ref<string>();
  const ratings = ref<TvRatingsData | undefined>(undefined);
  const ratingsForCSV = ref<ratingsForCSVResponse>([]);
  const topPrograms = ref<TvRating[]>([]);
  const avgHours = ref<AvgHours | undefined>(undefined);
  const excludeSports = ref(false);
  const includeAdultRating = ref(false);
  const resetting = ref(false);
  const selectedChartType = ref<string[]>([]);
  const chartFilters = ref<ChartFilter>({
    day_ranges: [],
    networks: [],
    dayparts: [],
  });
  const projects = ref<Project[]>([]);
  const comparison = ref<ComparisonData>();
  const comparisonGeo = ref<Market>();

  const baseProgramsParams = computed(() => {
    return {
      date_ranges: filters.value.date_ranges,
      tv_categories: filters.value.tv_categories,
      day_ranges: filters.value.day_ranges,
      dayparts: filters.value.dayparts,
    };
  });

  const formatParams = () => {
    let params = { ...filters.value };
    params = {
      ...params,
      excluded_genres: excludeSports.value ? ["Sports"] : [],
    };

    if (filters.value.genres?.includes("Spanish Language")) {
      params = { ...params, genres: [], languages: ["Spanish"] };
    }

    return { params };
  };

  const prepareRatingsPayload = () => {
    const { params } = formatParams();
    if (
      !params.meta_audience_id ||
      !params.geography ||
      !params.date_ranges ||
      !params.index_meta_audience_id
    ) {
      throw new Error("Missing required parameters");
    }
    const extraParams = [
      "favoriteName",
      "includeAdultRating",
      "comparisonPrograms",
      "project_id",
    ];
    extraParams.forEach((param: string) => {
      if (param in params) {
        delete (params as any)[param];
      }
    });
    const payload: paths["/api/v1/tv_ratings/ratings"]["post"]["requestBody"]["content"]["application/json"]["params"] =
      {
        meta_audience_id: params.meta_audience_id,
        geography: params.geography,
        date_ranges: params.date_ranges,
        index_meta_audience_id: params.index_meta_audience_id,
        ...params,
      };
    return { payload };
  };

  const { $ws } = usePlugins();

  return {
    // STATE
    loading,
    taskIds,
    errors,
    savingProject,
    filters,
    comparisonFilters,
    title,
    indexAudiences,
    dates,
    stations,
    programs,
    comparisonPrograms,
    comparisonStations,
    markets,
    current,
    previous,
    ratingsType,
    ratings,
    ratingsForCSV,
    topPrograms,
    avgHours,
    excludeSports,
    includeAdultRating,
    resetting,
    chartFilters,
    selectedChartType,
    projects,
    comparison,
    comparisonGeo,
    // MUTATIONS
    setFilters(value: Filters | undefined) {
      if (!value) {
        filters.value = defaultFilterState;
        comparisonFilters.value = defaultComparisonFilterState;
      } else {
        filters.value = value;
      }
    },
    setRatings(value: TvRatingsData) {
      if (!ratingsType.value || ratingsType.value === "ratings") {
        ratings.value = value;
      }
      if (!ratingsType.value || ratingsType.value === "top-programs") {
        if (value) {
          topPrograms.value = value.ratings!.slice(0, 10);
        } else {
          topPrograms.value = [];
        }
      }
      ratingsType.value = undefined;
    },

    // ACTIONS
    async getIndexAudiences() {
      loading.value.index_audiences = true;
      try {
        const { data } = await client.GET("/api/v1/tv_ratings/index_audiences");
        indexAudiences.value = (data ?? []) as Entity[];
      } catch (error) {
        console.error(error);
      } finally {
        loading.value.index_audiences = false;
      }
    },
    async getDateRange() {
      loading.value.dates = true;
      try {
        const { data, error } = await client.GET("/api/v1/tv_ratings/dates");
        if (error) {
          throw error;
        }
        taskIds.value.dates = data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "TvRatings",
            payload: { id: data?.task_id },
          });
        }
      } catch {
        loading.value.dates = false;
      }
    },
    async getMarketsForAudience(metaAudienceId: number, marketType: string) {
      if (!marketType || marketType === "national") return;
      loading.value.markets = true;
      try {
        const { data, error } = await client.GET(
          "/api/v1/tv_ratings/markets/{meta_audience_id}",
          {
            params: {
              path: { meta_audience_id: metaAudienceId },
              query: { market_type: marketType },
            },
          },
        );
        if (error) {
          throw error;
        }
        markets.value = (data ?? []) as Market[];
      } catch {
        errorStore.showError(errorStore.defaultErrorMessage);
      } finally {
        loading.value.markets = false;
      }
    },
    async getStations(comparisonTab: boolean, dmaId?: number) {
      loading.value.stations = true;
      let start_date, end_date;
      if (filters.value.date_ranges?.length) {
        start_date = filters.value.date_ranges[0];
        end_date = filters.value.date_ranges[1];
      }
      const args = {
        ...(dmaId && {
          dma_id: dmaId,
        }),
        start_date,
        end_date,
      };
      try {
        const { data } = await client.GET("/api/v1/tv_ratings/stations", {
          params: { query: args },
        });
        if (!comparisonTab) {
          stations.value = data?.stations ?? [];
        } else {
          comparisonStations.value = data?.stations ?? [];
        }
      } catch (error) {
        console.error(error);
      } finally {
        loading.value.stations = false;
      }
    },
    async getPrograms({ search }: GetProgramParams = {}) {
      loading.value.programs = true;
      const params = {
        ...baseProgramsParams.value,
        geography: filters.value.geography,
        stations: filters.value.stations,
        search: search,
      };
      try {
        const { data, error } = await client.POST(
          "/api/v1/tv_ratings/programs",
          {
            body: {
              params,
            } as unknown as schemas["TvRatingsProgramsGetRequest"],
          },
        );
        if (error) {
          throw error;
        }
        taskIds.value.programs = data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "TvRatings",
            payload: { id: data?.task_id },
          });
        }
      } catch {
        loading.value.programs = false;
      }
    },
    async getComparisonPrograms({ search }: GetProgramParams = {}) {
      loading.value.comparisonPrograms = true;
      const params = {
        ...baseProgramsParams.value,
        geography: comparisonFilters.value.geography,
        stations:
          comparisonFilters.value.compare === "stations"
            ? comparisonFilters.value.stations
            : [comparisonFilters.value.stations],
        search: search,
      };
      try {
        const { data, error } = await client.POST(
          "/api/v1/tv_ratings/programs",
          {
            body: {
              params,
            } as unknown as schemas["TvRatingsProgramsGetRequest"],
          },
        );
        if (error) {
          throw error;
        }
        taskIds.value.comparisonPrograms = data?.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "TvRatings",
            payload: { id: data?.task_id },
          });
        }
      } catch {
        loading.value.comparisonPrograms = false;
      }
    },

    async getAverageHours() {
      loading.value.avg_hours = true;
      errors.value.avgHours = undefined;
      try {
        const { payload } = prepareRatingsPayload();
        const { data } = await client.POST("/api/v1/tv_ratings/average_hours", {
          body: {
            params: payload,
            sync: false,
          },
        });
        if (
          data &&
          (!router.currentRoute.value ||
            router.currentRoute.value.name?.toString().includes("tv_ratings"))
        ) {
          taskIds.value.avg_hours = data.task_id;
          if ($ws) {
            $ws.subscribe({
              channel: "TvRatings",
              payload: { id: data.task_id },
            });
          }
        } else {
          loading.value.avg_hours = false;
        }
      } catch (e: any) {
        loading.value.avg_hours = false;
        if (
          router.currentRoute.value?.name?.toString().includes("tv_ratings")
        ) {
          errors.value.avgHours =
            e.message ||
            e.response?.data?.message ||
            errorStore.defaultErrorMessage;
        }
      }
    },
    async getComparison() {
      loading.value.comparison = true;
      comparison.value = undefined;
      errors.value.comparison = undefined;
      try {
        const { data, error } = await client.POST(
          "/api/v1/tv_ratings/comparison",
          {
            body: {
              params: {
                ...filters.value,
                ...comparisonFilters.value,
                stations:
                  comparisonFilters.value.compare === "stations"
                    ? comparisonFilters.value.stations
                    : [comparisonFilters.value.stations],
              },
            } as unknown as schemas["TvRatingsComparisonGetRequest"],
          },
        );
        if (error) {
          throw error;
        }
        if (
          !router.currentRoute.value ||
          router.currentRoute.value.name?.toString().includes("tv_ratings")
        ) {
          taskIds.value.comparison = data?.task_id;
          comparisonGeo.value = comparisonFilters.value.geography;
          if ($ws) {
            $ws.subscribe({
              channel: "TvRatings",
              payload: { id: data?.task_id },
            });
          }
        } else {
          loading.value.comparison = false;
        }
      } catch (e: any) {
        loading.value.comparison = false;
        if (router.currentRoute.value.name?.toString().includes("tv_ratings")) {
          errors.value.comparison =
            e?.message ||
            e?.response?.data?.message ||
            errorStore.defaultErrorMessage;
        }
      }
    },
    async getRatings() {
      loading.value.ratings = true;
      errors.value.overview = undefined;
      errors.value.ratings = undefined;
      errors.value.daypart = undefined;
      try {
        const { payload } = prepareRatingsPayload();
        const { data, error } = await client.POST(
          "/api/v1/tv_ratings/ratings",
          {
            body: {
              params: payload,
              sync: false,
            },
          },
        );
        if (
          data &&
          (!router.currentRoute.value ||
            router.currentRoute.value.name?.toString().includes("tv_ratings"))
        ) {
          taskIds.value.ratings = data.task_id;
          if ($ws) {
            $ws.subscribe({
              channel: "TvRatings",
              payload: { id: data.task_id },
            });
          }
        } else if (error) {
          throw error;
        }
      } catch (e: any) {
        loading.value.ratings = false;
        ratings.value = undefined;
        avgHours.value = undefined;
        if (
          router.currentRoute.value?.name?.toString().includes("tv_ratings")
        ) {
          const error =
            e.message ||
            e.response?.data?.message ||
            errorStore.defaultErrorMessage;
          errors.value.overview = error;
          errors.value.ratings = error;
          errors.value.daypart = error;
        }
      }
    },
    async getRatingsForCSV(): Promise<boolean> {
      loading.value.csv = true;
      try {
        const { payload } = prepareRatingsPayload();
        const { data } = await client.POST("/api/v1/tv_ratings/export", {
          body: {
            params: payload,
            sync: true,
          },
        });
        if (data?.length) {
          ratingsForCSV.value = data;
          return true;
        }
        ratingsForCSV.value = [];
        return false;
      } catch (e: any) {
        ratingsForCSV.value = [];
        errorStore.showError(
          e.message ||
            e.response?.data?.message ||
            errorStore.defaultErrorMessage,
        );
        return false;
      } finally {
        loading.value.csv = false;
      }
    },
    async createProject(project: Project) {
      savingProject.value = true;
      try {
        const { data, error } = await client.POST(
          "/api/v1/tv_ratings/projects",
          {
            body: project as unknown as schemas["TvRatingsCreateUpdateProjectBodyRequest"],
          },
        );
        if (error) {
          throw error;
        }
        if (data) {
          projects.value.push(data as Project);
        }
      } finally {
        savingProject.value = false;
      }
    },
    async updateProject(projectId: number, project: Project) {
      savingProject.value = true;
      try {
        const { data, error } = await client.PUT(
          "/api/v1/tv_ratings/projects/{project_id}",
          {
            params: { path: { project_id: projectId } },
            body: project as unknown as schemas["TvRatingsCreateUpdateProjectBodyRequest"],
          },
        );
        if (error) {
          throw error;
        }
        if (data) {
          updateInArray(projects.value, data as Project);
        }
      } finally {
        savingProject.value = false;
      }
    },
    async getProjects() {
      loading.value.projects = true;
      try {
        const { data } = await client.GET("/api/v1/tv_ratings/projects");
        projects.value = (data ?? []) as Project[];
      } catch (error) {
        console.error(error);
      } finally {
        loading.value.projects = false;
      }
    },
    async deleteProject(projectId: number) {
      savingProject.value = true;
      try {
        const { error } = await client.DELETE(
          "/api/v1/tv_ratings/projects/{project_id}",
          {
            params: { path: { project_id: projectId } },
          },
        );
        if (error) {
          throw error;
        }
        const idx = projects.value.findIndex((p) => p.id === projectId);
        if (idx >= 0) {
          projects.value.splice(idx, 1);
        }
      } finally {
        savingProject.value = false;
      }
    },
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTvRatingStore, import.meta.hot));
}
